import { BetaPill, Button, ModalV2 } from '@root/components'
import AccountingRuleForm from './AccountingRuleForm'
import AccountingRulePreviewResults, { PreviewOutcome } from './AccountingRulePreviewResults'
import {
  applyAccountingRule,
  createAccountingRule,
  fetchAutomationRulePreview,
  HttpError,
  IAccountingRule,
  updateAccountingRule
} from '@root/query'
import type { APIError } from '@root/utils'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FaArrowLeft, FaArrowRight, FaCheck, FaRedo } from 'react-icons/fa'
import { type FC, useCallback, useMemo, useState } from 'react'
import { useBusinessContext } from '@root/context'
import { queryKeyForAccountingRules } from '@root/query/hooks'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { isWorkflowOwnedRule } from './isWorkflowOwnedRule'

interface Props {
  isVisible: boolean
  handleOnClose: () => void
  rule?: Partial<IAccountingRule>
  handleOnSuccess?: () => void
  closeFormOnSuccess?: boolean
}

export type { PreviewOutcome }

const AccountingRuleFormModal: FC<Props> = ({
  isVisible,
  handleOnClose,
  rule,
  handleOnSuccess,
  closeFormOnSuccess = true
}) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()

  const isEdit = rule?.id != null
  const isWorkflowRule = isWorkflowOwnedRule(rule)

  // The rule saved from this modal. Tracked separately from the save mutation so
  // that returning to the form patches that same rule instead of creating another,
  // and so the preview step survives a preview that never resolves.
  const [savedRule, setSavedRule] = useState<IAccountingRule | null>(null)
  const [isPreviewStep, setIsPreviewStep] = useState(false)

  const previewQuery = useInfiniteQuery({
    queryKey: ['automation-rule-preview', slug, savedRule?.id],
    queryFn: ({ pageParam }) =>
      fetchAutomationRulePreview({ slug, ruleId: savedRule!.id, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: lastPage => lastPage.next ?? undefined,
    enabled: isPreviewStep && savedRule?.id != null,
    retry: false
  })

  const applyMutation = useMutation<void, unknown, { ruleId: number }>({
    mutationFn: ({ ruleId }) => applyAccountingRule({ slug, ruleId })
  })

  const targetRuleId = savedRule?.id ?? rule?.id ?? null

  const saveMutation = useMutation<IAccountingRule, HttpError<APIError>, Partial<IAccountingRule>>({
    mutationFn: (data: Partial<IAccountingRule>) =>
      targetRuleId != null
        ? updateAccountingRule(
            { slug, id: targetRuleId, skipMatching: true },
            { ...data, enabled: false }
          )
        : createAccountingRule({ slug, skipMatching: true }, { ...data, enabled: false }),
    onSuccess: saved => {
      setSavedRule(saved)
      setIsPreviewStep(true)
      queryClient.invalidateQueries({ queryKey: queryKeyForAccountingRules(slug) })
      queryClient.invalidateQueries({
        queryKey: ['automation-rule-preview', slug, saved.id]
      })
    }
  })

  const resetAndClose = useCallback(() => {
    applyMutation.reset()
    saveMutation.reset()
    queryClient.removeQueries({ queryKey: ['automation-rule-preview', slug] })
    setSavedRule(null)
    setIsPreviewStep(false)
    handleOnClose()
  }, [handleOnClose, applyMutation, saveMutation, queryClient, slug])

  const onApplyMatches = useCallback(async () => {
    const ruleId = savedRule?.id
    if (ruleId == null) return
    try {
      await applyMutation.mutateAsync({ ruleId })
      toast.success(t('settings.automation.accountingRules.form.applySuccess'))
      queryClient.invalidateQueries({ queryKey: queryKeyForAccountingRules(slug) })
      handleOnSuccess?.()
      if (closeFormOnSuccess) {
        resetAndClose()
      } else {
        applyMutation.reset()
        saveMutation.reset()
        setSavedRule(null)
        setIsPreviewStep(false)
      }
    } catch {
      toast.error(t('general.error'))
    }
  }, [
    savedRule,
    saveMutation,
    applyMutation,
    slug,
    t,
    queryClient,
    handleOnSuccess,
    closeFormOnSuccess,
    resetAndClose
  ])

  const onRetryPreview = useCallback(() => {
    previewQuery.refetch()
  }, [previewQuery])

  // The saved rule stays disabled, so returning to the form is safe: the next
  // save patches that same rule.
  const onBackToForm = useCallback(() => {
    setIsPreviewStep(false)
  }, [])

  const previewOutcome = useMemo((): PreviewOutcome => {
    const refreshing = previewQuery.isFetching && !previewQuery.isFetchingNextPage
    if (previewQuery.isPending || refreshing) {
      return { kind: 'loading' }
    }
    if (previewQuery.isError) {
      return { kind: 'failed' }
    }
    if (!previewQuery.data) {
      return { kind: 'loading' }
    }
    return {
      kind: 'matches',
      count: previewQuery.data.pages[0]?.count ?? 0,
      results: previewQuery.data.pages.flatMap(page => page.results)
    }
  }, [
    previewQuery.isPending,
    previewQuery.isFetching,
    previewQuery.isFetchingNextPage,
    previewQuery.isError,
    previewQuery.data
  ])

  const matchCount = previewOutcome.kind === 'matches' ? previewOutcome.count : 0

  const footer = isWorkflowRule ? (
    <StyledFooter>
      <Button type="button" onClick={resetAndClose}>
        {t('general.close')}
      </Button>
    </StyledFooter>
  ) : isPreviewStep ? (
    <StyledFooter>
      <StyledFooterActions>
        <Button type="button" onClick={resetAndClose} disabled={applyMutation.isPending}>
          {t('settings.automation.accountingRules.form.previewDismiss')}
        </Button>
        {previewOutcome.kind !== 'loading' && (
          <Button
            type="button"
            icon={<FaArrowLeft />}
            onClick={onBackToForm}
            disabled={applyMutation.isPending}
          >
            {t('settings.automation.accountingRules.form.previewBack')}
          </Button>
        )}
      </StyledFooterActions>
      <StyledFooterActions>
        {previewOutcome.kind === 'failed' && (
          <Button
            type="button"
            icon={<FaRedo />}
            onClick={onRetryPreview}
            disabled={applyMutation.isPending || previewQuery.isFetching}
          >
            {t('settings.automation.accountingRules.form.previewRetry')}
          </Button>
        )}
        {previewOutcome.kind === 'matches' && (
          <Button
            type="button"
            intent="success"
            icon={<FaCheck />}
            onClick={onApplyMatches}
            showSpinner={applyMutation.isPending}
          >
            {applyMutation.isPending
              ? t('settings.automation.accountingRules.form.previewApplying')
              : t('settings.automation.accountingRules.form.previewApplyCount', {
                  count: matchCount
                })}
          </Button>
        )}
      </StyledFooterActions>
    </StyledFooter>
  ) : (
    <StyledFooter>
      <Button type="button" onClick={resetAndClose} disabled={saveMutation.isPending}>
        {t('general.cancel')}
      </Button>
      <Button
        type="submit"
        form="automation-rule-form"
        intent="success"
        iconRight={<FaArrowRight />}
        showSpinner={saveMutation.isPending}
        disabled={saveMutation.isPending}
      >
        {t('settings.automation.accountingRules.form.saveAndPreview')}
      </Button>
    </StyledFooter>
  )

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={resetAndClose}
      header={t(
        rule?.id
          ? 'settings.automation.accountingRules.form.headerEdit'
          : 'settings.automation.accountingRules.form.headerCreate'
      )}
      headerIcon={
        <StyledHeaderExtras>
          <BetaPill label={t('general.beta.label')} tooltip={t('general.beta.tooltip')} />
          {!isWorkflowRule && (
            <Stepper>
              <StepperItem $state={isPreviewStep ? 'done' : 'active'}>
                <StepperCircle $state={isPreviewStep ? 'done' : 'active'}>
                  {isPreviewStep ? <FaCheck /> : '1'}
                </StepperCircle>
                {t('settings.automation.accountingRules.form.stepSetup')}
              </StepperItem>
              <StepperConnector />
              <StepperItem $state={isPreviewStep ? 'active' : 'idle'}>
                <StepperCircle $state={isPreviewStep ? 'active' : 'idle'}>2</StepperCircle>
                {t('settings.automation.accountingRules.form.stepPreview')}
              </StepperItem>
            </Stepper>
          )}
        </StyledHeaderExtras>
      }
      width={720}
      height={1100}
      autoHeight
      calm
      stackHeaderOnMobile
      footer={footer}
    >
      {isPreviewStep && !isWorkflowRule ? (
        <AccountingRulePreviewResults
          outcome={previewOutcome}
          isEdit={isEdit}
          isPurchaseInvoiceRule={Boolean(savedRule?.accrual_date_mode)}
          hasNextPage={previewQuery.hasNextPage}
          onLoadMore={() => {
            if (previewQuery.hasNextPage && !previewQuery.isFetchingNextPage) {
              previewQuery.fetchNextPage()
            }
          }}
        />
      ) : (
        <AccountingRuleForm rule={savedRule ?? rule} saveMutation={saveMutation} />
      )}
    </ModalV2>
  )
}

export default AccountingRuleFormModal

const StyledHeaderExtras = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-left: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    flex: none;
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
`

const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const StepperItem = styled.span<{ $state: 'active' | 'idle' | 'done' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-weight: ${({ $state }) => ($state === 'active' ? 600 : 400)};
  color: ${({ theme, $state }) =>
    $state === 'active' ? theme.colors.neutralBlack : theme.colors.iconGray};
`

const StepperCircle = styled.span<{ $state: 'active' | 'idle' | 'done' }>`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 0;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  background: ${({ theme, $state }) =>
    $state === 'active'
      ? theme.colors.primary
      : $state === 'done'
      ? theme.dashboard.positiveTint
      : theme.colors.neutralGray};
  color: ${({ theme, $state }) =>
    $state === 'active'
      ? theme.colors.neutralWhite
      : $state === 'done'
      ? theme.dashboard.positive
      : theme.colors.iconGray};

  && svg {
    width: 10px;
    height: 10px;
    padding: 0;
    cursor: default;
  }
`

const StepperConnector = styled.span`
  width: 16px;
  height: 1px;
  background: ${({ theme }) => theme.colors.borderStrong};
`

const StyledFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledFooterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
